import { Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { audienceForRole } from "@/lib/auth/portalGuard";

/**
 * DesignerGuard — gates the designer portal to signed-in users whose role is
 * `designer`. Mirrors `ClientGuard` but reads the STAFF auth context.
 *
 * The internal staff + client portals share ONE Supabase session, so this guard
 * only ever REDIRECTS (client-side) — it must NEVER call
 * `supabase.auth.signOut()`, which would kill a session the user legitimately
 * holds in the other portal. Wrong-role users are steered to their own home.
 */
interface DesignerGuardProps {
  children: React.ReactNode;
}

export const DesignerGuard = ({ children }: DesignerGuardProps) => {
  const { user, userRole, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    /* Straight to /signin: the legacy /auth redirect would drop `state`, and with it the deep link. */
    return <Navigate to="/signin" replace state={{ from: location.pathname + location.search }} />;
  }

  /**
   * Signed in but not a designer (incl. a missing role) → steer to their own
   * portal. Roleless users land on the staff entry, which shows AccountSetupError.
   */
  if (userRole !== "designer") {
    const home =
      audienceForRole(userRole) === "client" ? "/client/catalog" : "/design-requests";
    return <Navigate to={home} replace />;
  }

  return <>{children}</>;
};

export default DesignerGuard;
